// Visitor session storage. Under the bearer-token model the session JWT lives
// in first-party localStorage on the publisher domain (not a third-party
// cookie), so it works in browsers that block cross-site cookies (Safari,
// Brave, Firefox). The token is sent as an Authorization: Bearer header.

import type { ParkedSelection, TrialSetupResume } from '@/types';
import { createRequestScope, type RequestScope } from '@/requestScope';
import { decodeJwtPayload } from '@/tokenPayload';

const SESSION_KEY = 'ezsm_session';
const PENDING_CHECKOUT_KEY = 'ezsm_pending_checkout';
const PENDING_UPGRADE_KEY = 'ezsm_pending_upgrade';

export function setPendingUpgrade(upgradeId: string): void {
  try {
    localStorage.setItem(PENDING_UPGRADE_KEY, upgradeId);
  } catch {
    // Storage unavailable: authentication can only finish on this page.
  }
}

export function getPendingUpgrade(): string | null {
  try {
    return localStorage.getItem(PENDING_UPGRADE_KEY);
  } catch {
    return null;
  }
}

export function clearPendingUpgrade(): void {
  try {
    localStorage.removeItem(PENDING_UPGRADE_KEY);
  } catch {
    // Storage unavailable; no persisted marker can be removed.
  }
}
const PENDING_INTENT_KEY = 'ezsm_pending_intent';

// How long a persisted checkout intent stays resumable. Long enough to cover a
// redirect to the publisher's login and back, short enough that a stale intent
// can't re-open a checkout in a later session.
const PENDING_INTENT_TTL_MS = 30 * 60 * 1000;

interface StoredSession {
  jwt: string;
  expiresAt: string;
}

// PendingIntent captures the checkout a BYO visitor was in when they navigated
// away to the publisher's login, so it can be resumed (straight to payment for a
// logged-in visitor) when they return. The shared selection fields come from
// ParkedSelection (types.ts); originPath scopes resume to the page it started
// on and ts bounds it by TTL.
export interface PendingIntent extends ParkedSelection {
  originPath: string;
  ts: number;
  // The selector-form `anchor` the checkout was opened with, so the resumed
  // dialog lands in the same place. Element anchors cannot be stored.
  anchor?: string;
  // Set only while a trial card check (stripe.confirmSetup) is in flight: an
  // issuer 3DS redirect may unload the page, and the return resumes the trial
  // on this SetupIntent. No buyer credential is stored.
  trialSetup?: TrialSetupResume;
}

function readStoredSession(key: string): StoredSession | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (!parsed || typeof parsed.jwt !== 'string' || parsed.jwt === '') return null;
    return { jwt: parsed.jwt, expiresAt: typeof parsed.expiresAt === 'string' ? parsed.expiresAt : '' };
  } catch {
    return null;
  }
}

// readLiveSession returns the stored session at key, or null if absent/expired.
// Expired sessions are cleared as a side effect.
function readLiveSession(key: string): StoredSession | null {
  const session = readStoredSession(key);
  if (!session) return null;
  if (session.expiresAt) {
    const expMs = Date.parse(session.expiresAt);
    if (!Number.isNaN(expMs) && expMs <= Date.now()) {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
      return null;
    }
  }
  return session;
}

// getToken returns the stored session JWT, or null if absent/expired. Expired
// sessions are cleared as a side effect.
export function getToken(): string | null {
  return readLiveSession(SESSION_KEY)?.jwt ?? null;
}

export function hasSession(): boolean {
  return getToken() !== null;
}

// tokenVerified reports whether a session JWT carries the server's `vfy`
// (proven-email) claim. A UX hint only: the server enforces. Unparseable
// tokens are soft.
export function tokenVerified(jwt: string): boolean {
  return decodeJwtPayload(jwt)?.vfy === true;
}

// sessionVerified is tokenVerified for the stored session; absent or expired
// is soft.
export function sessionVerified(): boolean {
  const token = getToken();
  return token !== null && tokenVerified(token);
}

// keepsVerifiedSession reports whether the stored session is a verified one for
// the same visitor and domain as an incoming soft token. A checkout completion
// always returns a soft session, so adopting it would downgrade a buyer who had
// already verified. A different visitor's token still replaces the stored one.
export function keepsVerifiedSession(incoming: string): boolean {
  const stored = getToken();
  if (stored === null || tokenVerified(incoming) || !tokenVerified(stored)) {
    return false;
  }
  return sameSessionVisitor(stored, incoming);
}

// sameSessionVisitor reports whether two session JWTs name the same visitor on
// the same domain. Unparseable tokens never match.
export function sameSessionVisitor(a: string, b: string): boolean {
  const first = decodeJwtPayload(a);
  const second = decodeJwtPayload(b);
  if (!first || !second || first.vid === undefined) {
    return false;
  }
  return first.vid === second.vid && first.dom === second.dom;
}

// Marks the soft session token an ezAuth re-verify already left soft, so page
// loads don't repeat the PUA lookup for it. Keyed by a signature suffix, never
// the whole token; a new token misses naturally.
const SOFT_REVERIFY_KEY = 'ezsm_soft_reverify';

function tokenFingerprint(jwt: string): string {
  return jwt.slice(-12);
}

export function softReverifyTried(jwt: string): boolean {
  try {
    return sessionStorage.getItem(SOFT_REVERIFY_KEY) === tokenFingerprint(jwt);
  } catch {
    return false;
  }
}

export function markSoftReverifyTried(jwt: string): void {
  try {
    sessionStorage.setItem(SOFT_REVERIFY_KEY, tokenFingerprint(jwt));
  } catch {
    // Storage unavailable: the lookup repeats on the next page load.
  }
}

// setSession writes the slot unconditionally. Code storing a session from a
// response must go through beginSessionRequest().adopt/renew instead, or a late
// response can undo a sign-out; direct calls are for test setup.
export function setSession(jwt: string, expiresAt: string): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ jwt, expiresAt }));
  } catch {
    // Storage unavailable (private mode / disabled). Treat as anonymous.
  }
}

function removeSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
    // The marker belongs to this visitor; the next visitor cannot read it.
    localStorage.removeItem(PENDING_UPGRADE_KEY);
  } catch {
    // ignore
  }
}

// Sign-out invalidates every older operation. Initial sign-in also invalidates
// anonymous checks, but concurrent sign-ins must not cancel one another.
const signOutScope = createRequestScope();
const visitorScope = createRequestScope(signOutScope);

// clearSession is an explicit sign-out: every request started before it loses
// the right to write the visitor session.
export function clearSession(): void {
  signOutScope.advance();
  removeSession();
}

// SessionRequest is how a response writes the visitor session.
// Staleness is judged against the visitor session at begin (or at the
// operation's begin); token checks use the token this fetch actually sent.
export interface SessionRequest {
  isCurrent(): boolean;
  isSignOutCurrent(): boolean;
  // Identity write from a sign-in or completion. A stale request still lands
  // while someone is signed in (two sign-ins for one visitor, e.g. the ezAuth
  // checkout gate and watchEzAuthLogin); after a sign-out it is dropped and
  // reports false. Two different visitors on one tab are not told apart.
  adopt(jwt: string, expiresAt: string): boolean;
  // Sliding refresh of the visitor session from response headers; applies only
  // while the stored token is still the one this request sent.
  renew(jwt: string, expiresAt: string): void;
  // Dead-credential 401 cleanup: clears only the token this request sent.
  dropIfDead(): void;
}

export function beginSessionRequest(operation?: SessionRequest): SessionRequest {
  const sentToken = getToken();
  const isCurrent = operation ? operation.isCurrent : visitorScope.capture();
  const isSignOutCurrent = operation ? operation.isSignOutCurrent : signOutScope.capture();
  return {
    isCurrent,
    isSignOutCurrent,
    adopt: (jwt, expiresAt) => {
      if (!isSignOutCurrent()) {
        return false;
      }
      const signedIn = getToken() !== null;
      if (!isCurrent()) {
        if (!signedIn) {
          return false;
        }
        setSession(jwt, expiresAt);
        return true;
      }
      setSession(jwt, expiresAt);
      if (!signedIn) {
        visitorScope.advance();
      }
      return true;
    },
    renew: (jwt, expiresAt) => {
      if (getToken() === sentToken) {
        setSession(jwt, expiresAt);
      }
    },
    dropIfDead: () => {
      if (getToken() === sentToken) {
        removeSession();
      }
    },
  };
}

// Sandbox gate token: the preview token the sandbox instance's onsite gate
// requires on every request (sent in its own header, never as the bearer).
// Stored apart from the visitor session so the visitor-session lifecycle
// (clearSession on logout / dead-credential 401s / anonymous authChanged) can
// never wipe it. Cleared only by exitSandbox, a gate rejection, or its own
// expiry.
const SANDBOX_GATE_KEY = 'ezsm_sandbox_gate';

export function getSandboxGateToken(): string | null {
  return readLiveSession(SANDBOX_GATE_KEY)?.jwt ?? null;
}

export function setSandboxGateToken(jwt: string, expiresAt: string): void {
  try {
    localStorage.setItem(SANDBOX_GATE_KEY, JSON.stringify({ jwt, expiresAt }));
  } catch {
    // Storage unavailable (private mode / disabled). The preview lasts until
    // this page unload only.
  }
}

export function clearSandboxGateToken(): void {
  try {
    localStorage.removeItem(SANDBOX_GATE_KEY);
  } catch {
    // ignore
  }
}

// Sandbox-mode marker: presence means the widget is previewing the domain's
// sandbox catalog and talking to the sandbox instance (see sandboxMode.ts).
// Lives here, with the session it accompanies, so the API client can check it
// without importing the sandbox module (which itself imports the client).
const SANDBOX_MODE_KEY = 'ezsm_sandbox';

export function isSandboxMode(): boolean {
  try {
    return localStorage.getItem(SANDBOX_MODE_KEY) !== null;
  } catch {
    return false;
  }
}

export function setSandboxMode(): void {
  try {
    localStorage.setItem(SANDBOX_MODE_KEY, '1');
  } catch {
    // Storage unavailable: sandbox mode lasts until this page unload only.
  }
}

export function clearSandboxMode(): void {
  try {
    localStorage.removeItem(SANDBOX_MODE_KEY);
  } catch {
    // ignore
  }
}

// Whether the serving backend reported test keys (the site-config sandbox
// bit). Module memory: the client's config load sets it, an API re-point
// clears it. Read synchronously by checkout chrome (test-mode pill, test-card
// hint) that mounts after bootstrap's config load in practice — the same
// static-at-mount shape the old handoff-flag reads had.
let testKeysBackend = false;

export function setTestKeysBackend(testKeys: boolean): void {
  testKeysBackend = testKeys;
}

export function isTestKeysBackend(): boolean {
  return testKeysBackend;
}

// Ad-removal cookie teardown. adRemoval.ts owns the ez_sm_remove_ads
// lifecycle; the name and delete primitive live here so every sandbox-exit
// path can reach them — api/client.ts cannot import adRemoval (cycle:
// adRemoval imports the client for minting).
export const AD_REMOVAL_COOKIE_NAME = 'ez_sm_remove_ads';
// Nested in the visitor session: a sign-out or new sign-in also invalidates
// pending mints.
const adRemovalRequests = createRequestScope(visitorScope);
export const adRemovalScope: RequestScope = adRemovalRequests;

export function clearAdRemovalCookie(): void {
  // Invalidate even without a cookie: the first mint may still be pending.
  adRemovalRequests.advance();
  document.cookie = `${AD_REMOVAL_COOKIE_NAME}=; path=/; max-age=0; secure; samesite=lax`;
}

// Pending-checkout marker: set after Stripe confirms payment but before backend
// completion has established the session, so a reload mid-checkout can resume.
export function setPendingCheckout(checkoutId: string): void {
  try {
    localStorage.setItem(PENDING_CHECKOUT_KEY, checkoutId);
  } catch {
    // ignore
  }
}

export function getPendingCheckout(): string | null {
  try {
    return localStorage.getItem(PENDING_CHECKOUT_KEY) || null;
  } catch {
    return null;
  }
}

export function clearPendingCheckout(): void {
  try {
    localStorage.removeItem(PENDING_CHECKOUT_KEY);
  } catch {
    // ignore
  }
}

// setPendingIntent records the in-progress checkout before a BYO navigate-away
// login, stamped with the current path and time for resume scoping.
export function setPendingIntent(intent: Omit<PendingIntent, 'originPath' | 'ts'>): void {
  try {
    const stored: PendingIntent = { ...intent, originPath: window.location.pathname, ts: Date.now() };
    localStorage.setItem(PENDING_INTENT_KEY, JSON.stringify(stored));
  } catch {
    // Storage unavailable (private mode / disabled): resume simply won't happen.
  }
}

// getPendingIntent returns the stored intent, or null when absent, malformed, or
// past its TTL. An expired intent is cleared as a side effect.
export function getPendingIntent(): PendingIntent | null {
  try {
    const raw = localStorage.getItem(PENDING_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingIntent>;
    if (!parsed || (parsed.kind !== 'product' && parsed.kind !== 'item' && parsed.kind !== 'checkout') || typeof parsed.ts !== 'number' || typeof parsed.originPath !== 'string') {
      clearPendingIntent();
      return null;
    }
    if (Date.now() - parsed.ts > PENDING_INTENT_TTL_MS) {
      clearPendingIntent();
      return null;
    }
    return parsed as PendingIntent;
  } catch {
    return null;
  }
}

export function clearPendingIntent(): void {
  try {
    localStorage.removeItem(PENDING_INTENT_KEY);
  } catch {
    // ignore
  }
}

// Heartbeat throttle marker: the last time this browser sent a script-install
// heartbeat (ms epoch). One hour matches the backend's activity debounce
// window, so more frequent sends would only be discarded server-side.
const HEARTBEAT_KEY = 'ezsm_hb';
const HEARTBEAT_INTERVAL_MS = 60 * 60 * 1000;

// shouldSendHeartbeat reports whether a heartbeat is due, stamping the attempt
// when it is (a failed send consumes the slot — best-effort telemetry, the
// next hour retries). Storage unavailable → always due.
export function shouldSendHeartbeat(now: number = Date.now()): boolean {
  try {
    const last = Number(localStorage.getItem(HEARTBEAT_KEY) || 0);
    if (now - last < HEARTBEAT_INTERVAL_MS) {
      return false;
    }
    localStorage.setItem(HEARTBEAT_KEY, String(now));
  } catch {
    // Storage unavailable (private mode / disabled) — send anyway.
  }
  return true;
}
